
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/layout/Layout";
import { BlogFeaturedPost } from "@/components/blog/BlogFeaturedPost";
import { BlogPostCard, BlogPost as BlogPostType } from "@/components/blog/BlogPostCard";
import { BlogCategoryFilter } from "@/components/blog/BlogCategoryFilter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DatabaseBlogPost {
  id: string;
  title: string;
  description: string;
  category: string;
  featured: boolean;
  image_url: string | null;
  video_url: string | null;
  published_at: string;
  created_at: string;
}

export default function BlogPage() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<DatabaseBlogPost[]>([]);
  const [featuredPost, setFeaturedPost] = useState<DatabaseBlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch posts from the database
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .order('published_at', { ascending: false });
        
        if (error) throw error;
        
        if (data) {
          // Find the featured post (if any)
          const featured = data.find(post => post.featured);
          if (featured) {
            setFeaturedPost(featured);
            // Remove the featured post from the regular posts list
            setPosts(data.filter(post => post.id !== featured.id));
          } else {
            setPosts(data);
          }
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
        toast.error(t('blog.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [t]);

  // Filter posts by category
  const filteredPosts = selectedCategory 
    ? posts.filter(post => post.category === selectedCategory)
    : posts;

  // Get unique categories
  const categories = [...new Set(posts.map(post => post.category))];

  // Convert database post to component post format
  const mapDatabasePostToComponentPost = (dbPost: DatabaseBlogPost): BlogPostType => ({
    id: dbPost.id,
    title: dbPost.title,
    description: dbPost.description,
    image: dbPost.image_url || "/placeholder.svg",
    category: dbPost.category,
    date: dbPost.published_at,
    // You can add author information if available
    author: t('blog.admin.authorDefault')
  });

  return (
    <Layout>
      <div className="container max-w-7xl py-12 space-y-12">
        <div className="text-center space-y-4">
          <h1 className="text-3xl md:text-4xl font-bold">{t('blog.title')}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('blog.description')}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <>
            {featuredPost && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6">{t('blog.featured')}</h2>
                <BlogFeaturedPost post={mapDatabasePostToComponentPost(featuredPost)} />
              </div>
            )}

            <div className="space-y-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold">{t('blog.latestPosts')}</h2>
                <BlogCategoryFilter 
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                />
              </div>

              {filteredPosts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPosts.map(post => (
                    <BlogPostCard 
                      key={post.id}
                      post={mapDatabasePostToComponentPost(post)}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border rounded-md bg-muted/20">
                  <p className="text-muted-foreground">
                    {selectedCategory 
                      ? t('blog.noCategoryPosts', { category: selectedCategory })
                      : t('blog.noPosts')}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
